import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api.js';
import { queryKeys } from '../../lib/queryKeys.js';
import { Wordmark } from './Wordmark.jsx';
import { Input } from '../ui/Field.jsx';
import { Button } from '../ui/Button.jsx';
import { toast } from '../../stores/ui.js';

/**
 * The footer carries the full sitemap (PRD §6.2, item 12). The legal column is
 * built from the CMS rather than hardcoded, so a page an operator adds appears
 * here without a deploy — and a legal page a payment gateway checks for cannot
 * be quietly orphaned.
 */

export const Footer = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');

  const { data: groups } = useQuery({
    queryKey: queryKeys.pages.footer(),
    queryFn: () => api.get('/pages/footer'),
    staleTime: 15 * 60 * 1000,
  });

  const { data: settings } = useQuery({
    queryKey: queryKeys.settings.public(),
    queryFn: () => api.get('/settings/public'),
    staleTime: 15 * 60 * 1000,
  });

  const subscribe = useMutation({
    mutationFn: (address) => api.post('/newsletter', { email: address }),
    onSuccess: (result) => {
      setEmail('');
      setError('');
      toast({
        title: result.alreadySubscribed ? 'You are already on the list.' : 'You are on the list.',
        description: result.alreadySubscribed
          ? 'Nothing to do — we have this address already.'
          : 'Expect a note when something worth reading arrives.',
        tone: 'success',
      });
    },
    onError: (failure) => {
      setError(
        failure instanceof ApiError
          ? (failure.fields?.email ?? failure.message)
          : 'That did not go through. Try again in a moment.',
      );
    },
  });

  const shop = [
    { label: 'Everything', to: '/shop' },
    { label: 'Fragrance', to: '/shop?world=fragrance' },
    { label: 'Sport', to: '/shop?world=sport' },
    { label: 'Brands', to: '/brands' },
    { label: 'New arrivals', to: '/shop?isNewArrival=true' },
  ];

  const help = [
    { label: 'Track an order', to: '/orders/track' },
    { label: 'Contact us', to: '/contact' },
    { label: 'FAQ', to: '/faq' },
    { label: 'Store locator', to: '/store-locator' },
  ];

  const about = [
    { label: 'About us', to: '/about' },
    { label: 'Journal', to: '/journal' },
  ];

  const legal = (groups ?? []).find((group) => group.group === 'Legal')?.items ?? [];

  const socials = Object.entries(settings?.social ?? {}).filter(([, url]) => Boolean(url));

  return (
    <footer className="mt-section-lg border-t border-mist bg-surface">
      <div className="mx-auto max-w-(--container-content) px-4 sm:px-6">
        <section className="grid gap-8 border-b border-mist py-12 lg:grid-cols-[1fr_auto] lg:items-end lg:gap-16">
          <div>
            <h2 className="font-display text-xl tracking-[-0.02em] text-ink">
              Twelve fragrances. Nothing over ₹8,000.
            </h2>
            <p className="mt-2 max-w-md text-xs leading-relaxed text-muted">
              One note a month about what we have stocked and why. No countdowns, no manufactured
              urgency. Unsubscribe in one click.
            </p>
          </div>

          <form
            className="w-full lg:w-96"
            onSubmit={(event) => {
              event.preventDefault();
              if (!email.trim()) {
                setError('Enter an email address.');
                return;
              }
              subscribe.mutate(email.trim());
            }}
            noValidate
          >
            <div className="flex items-start gap-2">
              <Input
                type="email"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                  setError('');
                }}
                placeholder="you@example.com"
                aria-label="Email address"
                autoComplete="email"
                error={error}
                className="flex-1"
              />
              <Button type="submit" loading={subscribe.isPending} loadingLabel="Adding" className="shrink-0">
                Join
              </Button>
            </div>
          </form>
        </section>

        <nav aria-label="Footer" className="grid grid-cols-2 gap-8 py-12 sm:grid-cols-3 lg:grid-cols-5">
          <FooterColumn title="Shop" links={shop} />
          <FooterColumn title="Help" links={help} />
          <FooterColumn title="Company" links={about} />
          <FooterColumn
            title="Legal"
            links={legal.map((page) => ({ label: page.title, to: `/${page.slug}` }))}
          />

          <div className="col-span-2 sm:col-span-3 lg:col-span-1">
            <h3 className="font-mono text-2xs tracking-[0.14em] text-muted uppercase">Visit</h3>
            <address className="mt-3.5 space-y-1 text-xs leading-relaxed text-muted not-italic">
              <p className="text-ink">{settings?.contact?.address?.line1}</p>
              {settings?.contact?.address?.line2 && <p>{settings.contact.address.line2}</p>}
              <p>
                {settings?.contact?.address?.city} {settings?.contact?.address?.pincode}
              </p>
              {settings?.contact?.phone && (
                <p className="pt-2">
                  <a href={`tel:${settings.contact.phone.replace(/\s/g, '')}`} className="hover:text-ink">
                    {settings.contact.phone}
                  </a>
                </p>
              )}
              {settings?.contact?.email && (
                <p>
                  <a href={`mailto:${settings.contact.email}`} className="hover:text-ink">
                    {settings.contact.email}
                  </a>
                </p>
              )}
            </address>

            {socials.length > 0 && (
              <ul className="mt-4 flex flex-wrap gap-3">
                {socials.map(([name, url]) => (
                  <li key={name}>
                    <a
                      href={url}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="font-mono text-2xs tracking-[0.1em] text-muted uppercase transition-colors hover:text-ink"
                    >
                      {name}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </nav>

        <div className="flex flex-col gap-4 border-t border-mist py-7 sm:flex-row sm:items-center sm:justify-between">
          <Wordmark className="h-4" />
          <p className="font-mono text-2xs tracking-[0.06em] text-muted">
            © {new Date().getFullYear()} {settings?.store?.name ?? 'Golden Eagle'} · Prices include GST ·
            Made in India
          </p>
        </div>
      </div>
    </footer>
  );
};

const FooterColumn = ({ title, links }) => (
  <div>
    <h3 className="font-mono text-2xs tracking-[0.14em] text-muted uppercase">{title}</h3>
    <ul className="mt-3.5 space-y-2">
      {links.map((link) => (
        <li key={link.to}>
          <Link
            to={link.to}
            className="text-xs text-muted transition-colors hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            {link.label}
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

export default Footer;
